import {
  INDICE_ESTADO_MODIFICADO_DESDE_EL_SELLO,
  INDICE_SEVERIDAD_ALARMA,
  INDICE_SEVERIDAD_NINGUNA,
  INDICE_SEVERIDAD_SIN_VEREDICTO,
  type IndiceVerificacionResponse,
} from '../../../api/models/response/indiceDigital'
import { mineral } from '../../../theme'

/**
 * CÓMO SE PINTA UN VEREDICTO, Y DE DÓNDE SALE EL COLOR.
 *
 * El color lo decide EXCLUSIVAMENTE `severidad`. No hay —ni puede haber— una
 * comparación del tipo `estado !== Integro` en ningún sitio: el backend expone
 * `esAlarma` y `severidad` y deliberadamente NO expone un `esValido` precisamente
 * para que la pantalla no pueda pintar de rojo el caso normal.
 *
 * `ModificadoDesdeElSello` llega con severidad `Ninguna` porque un expediente de
 * sólo añadido que ha crecido está exactamente igual de sano que uno que no. Sale
 * en verde, con un parte de novedades al lado — nunca un aviso, nunca un icono de
 * alerta, nunca ámbar.
 */
export type VerificacionTone = 'ok' | 'neutral' | 'alarm'

export interface VerificacionVisual {
  tone: VerificacionTone
  /** Color del texto/icono del chip. */
  color: string
  /** Fondo del chip. */
  bg: string
  /** Clave i18n de la etiqueta corta del chip. */
  labelKey: string
  /** Clave i18n de la explicación larga, o null si no hay nada que explicar. */
  hintKey: string | null
  /** El expediente creció desde el sello: hay parte de novedades que enseñar. */
  hasNews: boolean
}

const TONES: Record<VerificacionTone, { color: string; bg: string }> = {
  ok: { color: mineral.sabanaDeep, bg: mineral.sabanaSoft },
  neutral: { color: mineral.pizarra, bg: mineral.pizarraSoft },
  alarm: { color: mineral.aji, bg: mineral.ajiSoft },
}

export function describeVerificacion(v: IndiceVerificacionResponse): VerificacionVisual {
  const grown = v.estado === INDICE_ESTADO_MODIFICADO_DESDE_EL_SELLO
  const hasNews = v.cambios != null

  // El switch va sobre severidad y sólo sobre severidad.
  switch (v.severidad) {
    case INDICE_SEVERIDAD_ALARMA:
      return {
        tone: 'alarm',
        ...TONES.alarm,
        labelKey: 'expedientes.electronico.verify.alarm',
        hintKey: 'expedientes.electronico.verify.alarmHint',
        hasNews,
      }
    case INDICE_SEVERIDAD_SIN_VEREDICTO:
      return {
        tone: 'neutral',
        ...TONES.neutral,
        labelKey: 'expedientes.electronico.verify.noVerdict',
        hintKey: 'expedientes.electronico.verify.noVerdictHint',
        hasNews,
      }
    case INDICE_SEVERIDAD_NINGUNA:
    default:
      // Íntegro y ModificadoDesdeElSello comparten tono. Lo único que cambia es
      // que el segundo añade un parte de novedades, en el mismo verde.
      return {
        tone: 'ok',
        ...TONES.ok,
        labelKey: grown
          ? 'expedientes.electronico.verify.grown'
          : 'expedientes.electronico.verify.sealIntact',
        hintKey: grown ? 'expedientes.electronico.verify.grownHint' : null,
        hasNews,
      }
  }
}

/**
 * `esAlarma` es lo ÚNICO que autoriza a la interfaz a decir que algo no cuadra con
 * lo sellado. Se expone como función con nombre para que cualquier condicional de
 * la vista tenga que pasar por aquí y no se cuele una comparación por estado.
 */
export function isAlarm(v: IndiceVerificacionResponse | null): boolean {
  return v?.esAlarma === true
}
